import React, { type ReactNode, useCallback, useState } from 'react';

import { IconButton } from '@atlaskit/button/new';
import ChevronLeftLargeIcon from '@atlaskit/icon/utility/migration/chevron-left--chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/utility/migration/chevron-right--chevron-right-large';
import { ExitingPersistence, mediumDurationMs, SlideIn } from '@atlaskit/motion';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { ProgressIndicator } from '@atlaskit/progress-indicator';

export type CarouselProps = {
	children: ReactNode;
	onSlideChange?: (args: { index: number; previousIndex: number }) => void;
	/**
	 * The height of the slides, not the full carousel
	 * Defaults to 405px, customize to match the design you are using
	 */
	height?: number;
};

const containerStyles = xcss({
	overflowX: 'hidden',
	width: '100%',
	paddingBlock: 'space.500',
	paddingInline: 'space.400',
});

export const Carousel = ({ children, onSlideChange, height = 405 }: CarouselProps) => {
	const [index, setIndex] = useState(0);

	const slides = React.Children.toArray(children);
	const slide = slides[index];
	const count = slides.length;

	const handleOnPrevious = useCallback(() => {
		const prevIndex = index - 1 < 0 ? count - 1 : index - 1;
		setIndex(prevIndex);
		onSlideChange?.({ index: prevIndex, previousIndex: index });
	}, [index, count, onSlideChange]);

	const handleOnNext = useCallback(() => {
		const nextIndex = index + 1 < count ? index + 1 : 0;
		setIndex(nextIndex);
		onSlideChange?.({ index: nextIndex, previousIndex: index });
	}, [index, count, onSlideChange]);

	const handleOnSelectIndex = useCallback(
		({ index: nextIndex }: { index: number }) => {
			setIndex(nextIndex);
			onSlideChange?.({ index: nextIndex, previousIndex: index });
		},
		[onSlideChange, index],
	);

	return (
		<Stack space="space.200" xcss={containerStyles} alignBlock="center">
			<ExitingPersistence exitThenEnter>
				<Box key={index}>
					<SlideIn enterFrom="right" exitTo="left" fade="inout" duration={mediumDurationMs}>
						{(props) => (
							<div style={{ height }} {...props} id={`slide${index}`}>
								{slide}
							</div>
						)}
					</SlideIn>
				</Box>
			</ExitingPersistence>
			<Inline alignBlock="center" alignInline="center">
				<IconButton
					appearance="subtle"
					icon={ChevronLeftLargeIcon}
					label="Previous"
					onClick={handleOnPrevious}
				/>
				<ProgressIndicator
					selectedIndex={index}
					values={slides.map((_, idx) => idx)}
					onSelect={handleOnSelectIndex}
					appearance="primary"
					ariaControls="slide"
				/>
				<IconButton
					appearance="subtle"
					icon={ChevronRightLargeIcon}
					label="Next"
					onClick={handleOnNext}
				/>
			</Inline>
		</Stack>
	);
};
